/**
 * CORE-M-02 Google Recommendations tab body.
 *
 * Chans's 2026-09-15 rebuild spec §2.2:
 *   Pull Google's recommendations into Core so we can see them. Prominent
 *   warning banner at the top: almost none should be accepted as-is. Apply /
 *   Ignore / Snooze per row, with evidence.
 *
 * What ships in phase A
 *
 * - The warning banner (G-1 voice, Chans's copy).
 * - The live list from `GET /google-ads/strategy-recs` (Strategy service —
 *   already gated by GOOGLE_ADS_STRATEGY_GOOGLE_RECS_ENABLED, default on).
 * - Per-row title, why, evidence chips (campaign, keyword, match type,
 *   "housekeeping" for core_offer=true).
 * - Dismiss: wired to POST /google-ads/strategy-recs/dismiss (goes through
 *   GoogleAdsWriter and the audit log).
 * - Snooze: per-viewer 30-day hide via localStorage (G-5). No shared state
 *   until a snooze table exists.
 * - Ignore: local session hide, no backend call. Same-tab refresh brings the
 *   row back.
 *
 * What is deliberately not here (V1 backend decision, commit 018b420ab):
 *
 * - Apply. The Strategy recommendations backend responds with
 *   `apply_enabled: false` for every row. The warning banner does the work
 *   the missing button would have done. Restoring Apply through the approval
 *   flow is a separate ticket.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowUpRight,
  BellOff,
  EyeOff,
  Loader2,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TablePagination, usePagination } from '@/components/ui/data-table';
import { EvidenceChip } from '@/components/google-ads/EvidenceChip';
import { AdsSlowLoad } from '@/features/ads/AdsSlowLoad';
import {
  dismissStrategyGoogleRec,
  listStrategyGoogleRecs,
  type StrategyGoogleRec,
} from '@/services/googleAdsManagementService';
import { useTenantStore } from '@/stores/useTenantStore';

import {
  SNOOZE_DURATION_DAYS,
  isSnoozed,
  snooze,
  unsnooze,
} from './snoozeStore';

interface GoogleRecommendationsTabProps {
  tenantId?: string | null;
}

function useGoogleRecs(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['management-google-recs', tenantId],
    queryFn: () => listStrategyGoogleRecs(tenantId ?? ''),
    enabled: Boolean(tenantId),
    staleTime: 60 * 1000,
    retry: 0,
    refetchOnWindowFocus: false,
  });
}

interface RowProps {
  rec: StrategyGoogleRec;
  tenantId: string;
  onDismiss: (resourceName: string) => void;
  onIgnore: (resourceName: string) => void;
  onSnooze: (resourceName: string) => void;
  dismissing: boolean;
}

function RecRow({
  rec,
  tenantId,
  onDismiss,
  onIgnore,
  onSnooze,
  dismissing,
}: RowProps) {
  const chips: Array<{ tone: 'neutral' | 'positive' | 'warning'; label: string; title?: string }> = [];
  if (rec.campaign_name) {
    chips.push({ tone: 'neutral', label: rec.campaign_name, title: `Campaign ${rec.campaign_name}` });
  }
  if (rec.keyword_text) {
    chips.push({ tone: 'neutral', label: `“${rec.keyword_text}”`, title: 'Keyword text' });
  }
  if (rec.match_type) {
    chips.push({ tone: 'neutral', label: rec.match_type, title: 'Match type' });
  }
  if (rec.core_offer) {
    chips.push({
      tone: 'positive',
      label: 'Housekeeping',
      title: 'Core reviewed this and considers it low-risk to apply in Google Ads',
    });
  } else {
    chips.push({
      tone: 'warning',
      label: 'Judgment call',
      title: 'Core has not classified this as safe to apply; read the reason before deciding',
    });
  }
  chips.push({ tone: 'neutral', label: rec.type, title: 'Google rec type' });

  return (
    <div className="rounded-md border border-border bg-warm-bg p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold">{rec.title}</div>
          {rec.why && (
            <p className="mt-1 text-sm text-muted-foreground">{rec.why}</p>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onSnooze(rec.resource_name)}
          >
            <BellOff className="mr-1.5 h-3.5 w-3.5" />
            Snooze {SNOOZE_DURATION_DAYS}d
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onIgnore(rec.resource_name)}
          >
            <EyeOff className="mr-1.5 h-3.5 w-3.5" />
            Ignore
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onDismiss(rec.resource_name)}
            disabled={dismissing}
            title="Tells Google to drop this recommendation. Uses the writer + audit log."
          >
            {dismissing ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="mr-1.5 h-3.5 w-3.5" />
            )}
            Dismiss
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {chips.map((c, i) => (
          <EvidenceChip key={`${rec.resource_name}-chip-${i}`} tone={c.tone} label={c.label} title={c.title} />
        ))}
        {tenantId ? null : null}
      </div>
    </div>
  );
}

/**
 * CORE-M-02.b: sort options over the visible rec list. Cost-impact and
 * date-flagged sorts named in the plan doc require fields the backend
 * response doesn't carry today (StrategyGoogleRec has no cost/timestamp);
 * this MVP set stays within the shipping shape. Add cost/date once the
 * backend surfaces them.
 */
type RecSortKey =
  | 'type-asc'
  | 'title-asc'
  | 'campaign-asc'
  | 'housekeeping-first';

const REC_SORT_OPTIONS: ReadonlyArray<{ key: RecSortKey; label: string }> = [
  { key: 'type-asc', label: 'Recommendation type (A–Z)' },
  { key: 'title-asc', label: 'Title (A–Z)' },
  { key: 'campaign-asc', label: 'Campaign (A–Z)' },
  { key: 'housekeeping-first', label: 'Housekeeping first' },
];

const REC_PAGE_URL_PARAM = 'page';
const REC_DEFAULT_PAGE_SIZE = 25;

function compareRecsBy(key: RecSortKey, a: StrategyGoogleRec, b: StrategyGoogleRec): number {
  switch (key) {
    case 'type-asc':
      return a.type.localeCompare(b.type) || a.title.localeCompare(b.title);
    case 'title-asc':
      return a.title.localeCompare(b.title);
    case 'campaign-asc': {
      const av = a.campaign_name ?? '';
      const bv = b.campaign_name ?? '';
      return av.localeCompare(bv) || a.title.localeCompare(b.title);
    }
    case 'housekeeping-first': {
      // core_offer=true is Core's low-risk stamp — show those first.
      if (a.core_offer !== b.core_offer) return a.core_offer ? -1 : 1;
      return a.type.localeCompare(b.type);
    }
    default:
      return 0;
  }
}

export function GoogleRecommendationsTab({ tenantId: tenantIdProp }: GoogleRecommendationsTabProps) {
  const storeTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = (tenantIdProp === undefined ? storeTenant : tenantIdProp) ?? null;
  const q = useGoogleRecs(tenantId);
  const client = useQueryClient();

  const [locallyHidden, setLocallyHidden] = useState<Set<string>>(() => new Set());
  const [snoozeVersion, setSnoozeVersion] = useState(0);
  const bumpSnooze = useCallback(() => setSnoozeVersion((v) => v + 1), []);

  // CORE-M-02.b: sort + filter state. Sort defaults to type-asc so the
  // list groups by Google's recommendation type — an operator scanning
  // for "which BID recs are here" gets them together without touching
  // the dropdown. Type filter is multi-select via chip toggles.
  const [sortKey, setSortKey] = useState<RecSortKey>('type-asc');
  const [typeFilter, setTypeFilter] = useState<Set<string>>(() => new Set());
  const toggleTypeFilter = useCallback((type: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);
  const clearTypeFilter = useCallback(() => setTypeFilter(new Set()), []);

  const dismissMutation = useMutation({
    mutationFn: (resourceName: string) => dismissStrategyGoogleRec(tenantId ?? '', resourceName),
    onSuccess: (_res, resourceName) => {
      // Remove the row from the cached list so it disappears top-to-bottom
      // per G-6 (actioned rows disappear immediately, before the refetch).
      client.setQueryData(['management-google-recs', tenantId], (prev: any) => {
        if (!prev || !Array.isArray(prev.items)) return prev;
        return {
          ...prev,
          items: prev.items.filter((r: StrategyGoogleRec) => r.resource_name !== resourceName),
          total: Math.max(0, (prev.total ?? prev.items.length) - 1),
        };
      });
      // A background refetch reconciles Google's own state.
      client.invalidateQueries({ queryKey: ['management-google-recs', tenantId] });
    },
  });

  const handleDismiss = useCallback(
    (resourceName: string) => {
      if (!tenantId) return;
      dismissMutation.mutate(resourceName);
    },
    [dismissMutation, tenantId],
  );

  const handleIgnore = useCallback((resourceName: string) => {
    setLocallyHidden((prev) => {
      const next = new Set(prev);
      next.add(resourceName);
      return next;
    });
  }, []);

  const handleSnooze = useCallback(
    (resourceName: string) => {
      snooze(tenantId, resourceName);
      bumpSnooze();
    },
    [tenantId, bumpSnooze],
  );

  const visibleRecs = useMemo(() => {
    void snoozeVersion;
    const raw = q.data?.items ?? [];
    return raw.filter(
      (r) => !locallyHidden.has(r.resource_name) && !isSnoozed(tenantId, r.resource_name),
    );
  }, [q.data, locallyHidden, snoozeVersion, tenantId]);

  // CORE-M-02.b: the set of recommendation types that appear on the current
  // tenant's list — the filter chip row is built from these so we never
  // render a chip for a type Google isn't returning today.
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    for (const r of visibleRecs) set.add(r.type);
    return Array.from(set).sort();
  }, [visibleRecs]);

  const filteredRecs = useMemo(() => {
    if (typeFilter.size === 0) return visibleRecs;
    return visibleRecs.filter((r) => typeFilter.has(r.type));
  }, [visibleRecs, typeFilter]);

  const sortedRecs = useMemo(() => {
    const copy = filteredRecs.slice();
    copy.sort((a, b) => compareRecsBy(sortKey, a, b));
    return copy;
  }, [filteredRecs, sortKey]);

  // CORE-M-02.b: URL-sync `?page=N`, default (page 1) is dropped from the
  // URL. Same pattern W-03 uses for the leads date range and BP-04 uses
  // for the pacing history window.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPage = (() => {
    const raw = searchParams.get(REC_PAGE_URL_PARAM);
    if (!raw) return 1;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  })();
  const pagination = usePagination(sortedRecs.length, {
    defaultPageSize: REC_DEFAULT_PAGE_SIZE,
  });
  const { page, pageSize, setPage, setPageSize, pageCount, slice } = pagination;
  // Bring URL → pagination whenever the URL page changes (paste-in link,
  // browser back/forward). One-way; the reverse direction runs from the
  // pager's onPageChange below so the URL only updates when the operator
  // actually pages, not on every re-render.
  useEffect(() => {
    if (urlPage !== page && urlPage >= 1 && urlPage <= pageCount) {
      setPage(urlPage);
    }
    // pageCount changes with the filter/sort output; only re-check when
    // the URL or the derived pageCount changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage, pageCount]);
  const handlePageChange = useCallback(
    (next: number) => {
      setPage(next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next <= 1) p.delete(REC_PAGE_URL_PARAM);
          else p.set(REC_PAGE_URL_PARAM, String(next));
          return p;
        },
        { replace: true },
      );
    },
    [setPage, setSearchParams],
  );
  const pageRecs = slice(sortedRecs);

  const snoozedCount = useMemo(() => {
    void snoozeVersion;
    const raw = q.data?.items ?? [];
    return raw.filter((r) => isSnoozed(tenantId, r.resource_name)).length;
  }, [q.data, snoozeVersion, tenantId]);

  const unsnoozeAll = useCallback(() => {
    const raw = q.data?.items ?? [];
    for (const r of raw) unsnooze(tenantId, r.resource_name);
    bumpSnooze();
  }, [q.data, tenantId, bumpSnooze]);

  const banner = (
    <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm dark:border-amber-700 dark:bg-amber-950/40">
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-700 dark:text-amber-300" />
      <div className="text-amber-900 dark:text-amber-200">
        <p className="font-semibold">Google's recommendations here are a reference.</p>
        <p className="mt-1">
          Almost none should be accepted as-is. Read the reasoning, then decide.
          Applying a recommendation in Google Ads is deliberate — this tab lists
          them so you can see them, dismiss the ones that are wrong, and snooze
          the ones you want to revisit.
        </p>
      </div>
    </div>
  );

  if (!tenantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No workspace selected</CardTitle>
          <CardDescription>Pick a workspace to see its Google recommendations.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        {banner}
        <AdsSlowLoad
          label="Reading Google recommendations"
          tenantId={tenantId}
        />
      </div>
    );
  }

  if (q.isError) {
    return (
      <div className="space-y-4">
        {banner}
        <Card>
          <CardHeader>
            <CardTitle>Could not read Google recommendations</CardTitle>
            <CardDescription>
              Core could not reach the Google Ads recommendations API for{' '}
              <span className="font-medium text-foreground">{tenantId}</span>.
              Check the Google Ads connection in Settings, under Integrations,
              then reload.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const readOk = q.data?.read_ok ?? true;
  const readMessage = q.data?.message ?? null;

  return (
    <div className="space-y-4">
      {banner}

      {!readOk && (
        <Card>
          <CardHeader>
            <CardTitle>Recommendations are not being read right now</CardTitle>
            <CardDescription>{readMessage || 'The Strategy recommendations flag is off for this deploy.'}</CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {q.data?.total ?? 0} open recommendation{(q.data?.total ?? 0) === 1 ? '' : 's'}
          {snoozedCount > 0 && (
            <>
              {' '}
              <Badge
                variant="outline"
                className="ml-2 border-border bg-muted/40 text-muted-foreground"
              >
                {snoozedCount} snoozed
              </Badge>
            </>
          )}
          {locallyHidden.size > 0 && (
            <>
              {' '}
              <Badge
                variant="outline"
                className="ml-2 border-border bg-muted/40 text-muted-foreground"
              >
                {locallyHidden.size} ignored this session
              </Badge>
            </>
          )}
        </div>
        {snoozedCount > 0 && (
          <Button size="sm" variant="ghost" onClick={unsnoozeAll}>
            Unsnooze all
          </Button>
        )}
      </div>

      {visibleRecs.length === 0 ? (
        <Card>
          <CardHeader>
            {/* Dev walk 2026-09-24: say what this tab is (Google's list,
                each row labelled by Core's core_offer chip) so its empty
                state cannot pass for another's. */}
            <CardTitle>No Google recommendations to show</CardTitle>
            <CardDescription>
              {(q.data?.total ?? 0) === 0
                ? 'Google has no open recommendations for this account right now. This tab lists Google\'s own suggestions, each labelled by Core as Housekeeping or Judgment call.'
                : `Google has ${q.data?.total} open recommendation${q.data?.total === 1 ? '' : 's'} for this account, and you have ignored or snoozed all of them.`}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* CORE-M-02.b: sort + filter row sits ABOVE the list. Type-filter
              chips are built from the types actually present on this tenant
              so we never render a chip for a type Google isn't returning. */}
          <div
            className="flex flex-col gap-2 rounded-md border border-border bg-warm-bg p-3"
            data-testid="recs-sort-filter"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm">
                <label
                  htmlFor="recs-sort"
                  className="font-medium text-muted-foreground"
                >
                  Sort by
                </label>
                <Select
                  value={sortKey}
                  onValueChange={(value) => setSortKey(value as RecSortKey)}
                >
                  <SelectTrigger
                    id="recs-sort"
                    className="h-8 w-[220px]"
                    aria-label="Sort recommendations by"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REC_SORT_OPTIONS.map((option) => (
                      <SelectItem key={option.key} value={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {typeFilter.size > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearTypeFilter}
                  aria-label="Clear recommendation type filter"
                >
                  Clear filter
                </Button>
              )}
            </div>
            {availableTypes.length > 1 && (
              <div
                className="flex flex-wrap items-center gap-1.5"
                role="group"
                aria-label="Filter by recommendation type"
              >
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  Type
                </span>
                {availableTypes.map((type) => {
                  const active = typeFilter.has(type);
                  return (
                    <Button
                      key={type}
                      type="button"
                      size="sm"
                      variant={active ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      aria-pressed={active}
                      onClick={() => toggleTypeFilter(type)}
                    >
                      {type}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          {sortedRecs.length === 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>No recommendations match this filter</CardTitle>
                <CardDescription>
                  Clear the type filter above to see the full list again.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <>
              <div className="space-y-2">
                {pageRecs.map((rec) => (
                  <RecRow
                    key={rec.resource_name}
                    rec={rec}
                    tenantId={tenantId}
                    onDismiss={handleDismiss}
                    onIgnore={handleIgnore}
                    onSnooze={handleSnooze}
                    dismissing={
                      dismissMutation.isPending &&
                      dismissMutation.variables === rec.resource_name
                    }
                  />
                ))}
              </div>
              <TablePagination
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                totalRows={sortedRecs.length}
                onPageChange={handlePageChange}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 25, 50]}
              />
            </>
          )}
        </>
      )}

      <p className="flex items-start gap-2 pt-2 text-xs text-muted-foreground">
        <ArrowUpRight className="h-3.5 w-3.5 flex-shrink-0" />
        This tab is view-only by design. When a recommendation should
        actually run, do it in Google Ads so the change goes through Google's
        own review, and Core will pick it up on the next read.
      </p>
    </div>
  );
}

export default GoogleRecommendationsTab;
